/* Filter only entries the Liquid publication gate rendered for customers. */
if (!customElements.get('akumi-knowledge-page')) {
  customElements.define('akumi-knowledge-page', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.media = matchMedia('(max-width: 960px)');
      const setToc = () => this.querySelectorAll('[data-knowledge-toc]').forEach(toc => { toc.open = !this.media.matches; });
      const openDestination = () => {
        let id;
        try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
        const target = document.getElementById(id);
        if (target && this.contains(target)) {
          const question = target.querySelector('.faq-item');
          if (question) question.open = true;
        }
      };
      this.media.addEventListener('change', setToc, options);
      window.addEventListener('hashchange', openDestination, options);
      setToc();
      openDestination();
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}
if (!customElements.get('akumi-knowledge-index')) {
  customElements.define('akumi-knowledge-index', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.form = this.querySelector('[data-guide-search]');
      this.search = this.querySelector('[data-guide-query]');
      this.category = this.querySelector('[data-guide-category]');
      this.status = this.querySelector('[data-guide-status]');
      this.empty = this.querySelector('[data-guide-empty]');
      this.entries = [...this.querySelectorAll('[data-guide-entry]')];
      this.groups = [...this.querySelectorAll('[data-guide-group]')];
      this.statePath = location.pathname;
      this.restoring = true;
      this.lastEntryHref = '';
      this.media = matchMedia('(max-width: 700px)');
      const setGroups = () => {
        const hasFilter = Boolean(this.search?.value.trim() || this.category?.value);
        this.querySelectorAll('[data-guide-group-details]').forEach(group => { group.open = hasFilter || !this.media.matches; });
      };
      this.media.addEventListener('change', () => { setGroups(); this.scheduleSave(); }, options);
      setGroups();
      this.normalize = value => value.normalize('NFKC').toLocaleLowerCase('ja').trim();
      this.entries.forEach(entry => {
        entry.searchText = this.normalize(entry.dataset.search || entry.textContent || '');
      });
      this.form?.removeAttribute('hidden');
      const updateResults = () => {
        this.lastEntryHref = '';
        this.filter();
        this.scheduleSave();
      };
      this.form?.addEventListener('submit', event => { event.preventDefault(); updateResults(); }, options);
      this.search?.addEventListener('input', updateResults, options);
      this.search?.addEventListener('search', updateResults, options);
      this.category?.addEventListener('change', updateResults, options);
      this.querySelectorAll('[data-guide-reset]').forEach(button => {
        button.addEventListener('click', () => {
          if (this.search) this.search.value = '';
          if (this.category) this.category.value = '';
          updateResults();
          this.search?.focus();
        }, options);
      });
      this.groups.forEach(group => group.addEventListener('toggle', () => this.scheduleSave(), options));
      this.addEventListener('click', event => {
        const link = event.target?.closest?.('[data-guide-entry] a[href]');
        if (link && this.contains(link) && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
          this.lastEntryHref = link.getAttribute('href');
          this.saveState();
        }
      }, options);
      window.addEventListener('scroll', () => this.scheduleSave(), { ...options, passive: true });
      window.addEventListener('pagehide', () => this.saveState(), options);
      window.addEventListener('pageshow', () => this.restoreState(), options);
      window.addEventListener('popstate', () => this.restoreState(), options);
      if (!this.restoreState()) {
        this.filter();
        this.restoring = false;
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.saveTimer);
      cancelAnimationFrame(this.restoreFrame);
    }
    scheduleSave() {
      if (this.restoring) return;
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.saveState(), 180);
    }
    groupKey(group) {
      return group.querySelector('[data-guide-entry]')?.dataset.category || '';
    }
    saveState() {
      clearTimeout(this.saveTimer);
      if (this.restoring || location.pathname !== this.statePath) return;
      const current = history.state;
      if (current !== null && typeof current !== 'object') return;
      const snapshot = {
        version: 1,
        query: (this.search?.value || '').slice(0, 2000),
        category: this.category?.value || '',
        openGroups: this.groups.filter(group => group.open).map(group => this.groupKey(group)),
        scrollX: Math.max(0, Math.round(window.scrollX)),
        scrollY: Math.max(0, Math.round(window.scrollY)),
        entryHref: this.lastEntryHref || ''
      };
      try {
        history.replaceState({ ...current, akumiGuideIndex: { ...current?.akumiGuideIndex, [this.statePath]: snapshot } }, '');
      } catch {
        // Filtering still works when browser history storage is unavailable.
      }
    }
    restoreState() {
      if (location.pathname !== this.statePath) return false;
      const snapshot = history.state?.akumiGuideIndex?.[this.statePath];
      if (!snapshot || snapshot.version !== 1) return false;
      clearTimeout(this.saveTimer);
      cancelAnimationFrame(this.restoreFrame);
      this.restoring = true;
      if (this.search) this.search.value = typeof snapshot.query === 'string' ? snapshot.query.slice(0, 2000) : '';
      if (this.category) {
        this.category.value = [...this.category.options].some(option => option.value === snapshot.category) ? snapshot.category : '';
      }
      this.lastEntryHref = typeof snapshot.entryHref === 'string' ? snapshot.entryHref : '';
      this.filter(false);
      const openGroups = new Set(Array.isArray(snapshot.openGroups) ? snapshot.openGroups : []);
      this.groups.forEach(group => { group.open = openGroups.has(this.groupKey(group)); });
      this.restoreFrame = requestAnimationFrame(() => {
        this.restoreFrame = requestAnimationFrame(() => {
          const link = [...this.querySelectorAll('[data-guide-entry]:not([hidden]) a[href]')].find(item => item.getAttribute('href') === this.lastEntryHref);
          const linkGroup = link?.closest('[data-guide-group]');
          if (link && !linkGroup?.hidden && (!linkGroup || linkGroup.open)) link.focus({ preventScroll: true });
          window.scrollTo({ left: Number.isFinite(snapshot.scrollX) ? Math.max(0, snapshot.scrollX) : 0, top: Number.isFinite(snapshot.scrollY) ? Math.max(0, snapshot.scrollY) : 0, behavior: 'instant' });
          this.restoring = false;
        });
      });
      return true;
    }
    filter(expandMatches = true) {
      const terms = this.normalize(this.search?.value || '').split(/\s+/).filter(Boolean);
      const category = this.category?.value || '';
      let count = 0;
      this.entries.forEach(entry => {
        const visible = (!category || entry.dataset.category === category) && terms.every(term => entry.searchText.includes(term));
        entry.hidden = !visible;
        if (visible) count += 1;
      });
      this.groups.forEach(group => {
        group.hidden = !group.querySelector('[data-guide-entry]:not([hidden])');
        const disclosure = group.matches('details') ? group : group.querySelector('details');
        if (disclosure && expandMatches && (terms.length || category)) disclosure.open = true;
      });
      if (this.empty) this.empty.hidden = count > 0;
      if (this.status) this.status.textContent = (this.dataset.countLabel || '[count]件のガイド').replace('[count]', String(count));
    }
  });
}
